<template>
  <div class="bg-gray-100 min-h-screen">
    <v-container fluid class="px-4 py-8 max-w-none">
      <!-- Plan Upgrade Section -->
      <div class="mb-12">
        <div class="flex justify-center mb-8">
          <div class="bg-white p-1 rounded-full border border-gray-200 inline-flex shadow-sm">
            <button
              class="px-6 py-2 rounded-full text-sm font-bold transition-all duration-200 focus:outline-none"
              :class="
                billingCycle === 'monthly'
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'text-gray-500 hover:text-gray-900'
              "
              @click="billingCycle = 'monthly'"
            >
              Monthly
            </button>
            <button
              class="px-6 py-2 rounded-full text-sm font-bold transition-all duration-200 focus:outline-none flex items-center"
              :class="
                billingCycle === 'yearly'
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'text-gray-500 hover:text-gray-900'
              "
              @click="billingCycle = 'yearly'"
            >
              Yearly
              <span
                v-if="billingCycle !== 'yearly'"
                class="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full"
              >
                -20%
              </span>
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <v-card
            v-for="plan in currentPlans"
            :key="plan.name"
            class="flex flex-col h-full rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl cursor-pointer relative border"
            :class="[
              selectedPlan === plan.name
                ? `ring-2 ring-offset-2 ring-${plan.colorName}-500 shadow-lg transform scale-[1.02]`
                : 'border-gray-100'
            ]"
            @click="selectedPlan = plan.name"
          >
            <!-- Popular Badge -->
            <div
              v-if="plan.popular"
              class="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg z-10"
            >
              POPULAR
            </div>

            <!-- Selection Indicator -->
            <div v-if="selectedPlan === plan.name" class="absolute top-3 left-3 text-blue-500">
              <v-icon color="blue">mdi-check-circle</v-icon>
            </div>

            <!-- Plan Header -->
            <div class="p-6 text-center bg-white border-b border-gray-50">
              <h3
                class="text-lg font-bold mb-2 uppercase tracking-wide"
                :class="`text-${plan.colorName}-600`"
              >
                {{ plan.name }}
              </h3>
              <div class="flex justify-center items-baseline mb-1">
                <span class="text-4xl font-extrabold text-gray-900">₹{{ plan.price }}</span>
                <span class="text-gray-500 ml-1 font-medium text-sm"
                  >/{{ billingCycle === 'monthly' ? 'mo' : 'yr' }}</span
                >
              </div>
              <p class="text-xs text-gray-400 mt-1 h-4">
                {{ billingCycle === 'yearly' ? 'Billed annually' : '' }}
              </p>
            </div>

            <!-- Features -->
            <div class="p-6 flex-grow bg-gray-50/50">
              <ul class="space-y-4">
                <li v-for="feature in plan.features" :key="feature" class="flex items-start">
                  <v-icon small color="green" class="mt-0.5 mr-3 flex-shrink-0"
                    >mdi-check-circle</v-icon
                  >
                  <span class="text-sm text-gray-600 font-medium leading-relaxed text-left">{{
                    feature
                  }}</span>
                </li>
              </ul>
            </div>

            <!-- Action -->
            <div class="p-6 bg-white mt-auto">
              <v-btn
                block
                large
                depressed
                :color="plan.name === 'Base' ? 'grey lighten-3' : 'primary'"
                :class="plan.name === 'Base' ? 'grey--text text--darken-2' : 'white--text'"
                class="rounded-lg font-bold tracking-wide"
                @click.stop="purchasePlan(plan)"
                :loading="loadingPlan === plan.name"
                :disabled="plan.name === 'Base'"
              >
                {{ plan.name === 'Base' ? 'Current Plan' : 'Choose ' + plan.name }}
              </v-btn>
            </div>
          </v-card>
        </div>
      </div>

      <v-divider class="mb-8"></v-divider>

      <!-- Header -->
      <div class="flex justify-between items-center mb-8">
        <h1 class="text-xl font-bold text-gray-800">Module & Feature Permissions</h1>
      </div>

      <!-- Legend -->
      <div class="mb-8">
        <div class="flex flex-wrap gap-4 mb-4 w-fit">
          <span
            class="px-4 py-2 rounded-full text-xs font-semibold bg-green-100 text-green-800 flex items-center border border-green-400"
          >
            <v-icon small class="mr-1">mdi-check-circle</v-icon> Allow
          </span>
          <span
            class="px-4 py-2 rounded-full text-xs font-semibold bg-red-100 text-red-800 flex items-center border border-red-400"
          >
            <v-icon small class="mr-1">mdi-lock</v-icon> Deny
          </span>
          <span
            class="px-4 py-2 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 flex items-center border border-blue-400"
          >
            <v-icon small class="mr-1">mdi-toggle-switch</v-icon> Enabled
          </span>
          <span
            class="px-4 py-2 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 flex items-center border border-gray-400"
          >
            <v-icon small class="mr-1">mdi-toggle-switch-off</v-icon> Disabled
          </span>
        </div>

        <!-- Summary Card -->
        <v-card class="p-4">
          <h2 class="text-xl font-semibold mb-4">Summary</h2>
          <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div class="bg-blue-50 p-4 rounded-lg">
              <div class="text-blue-800 font-bold text-lg">{{ summaryStats.totalModules }}</div>
              <div class="text-blue-600">Modules</div>
            </div>
            <div class="bg-green-50 p-4 rounded-lg">
              <div class="text-green-800 font-bold text-lg">{{ summaryStats.enabledModules }}</div>
              <div class="text-green-600">Enabled Modules</div>
            </div>
            <div class="bg-yellow-50 p-4 rounded-lg">
              <div class="text-yellow-800 font-bold text-lg">
                {{ summaryStats.modulesWithoutFeatures }}
              </div>
              <div class="text-yellow-600">Modules with no features</div>
            </div>
            <div class="bg-indigo-50 p-4 rounded-lg">
              <div class="text-indigo-800 font-bold text-lg">{{ summaryStats.totalFeatures }}</div>
              <div class="text-indigo-600">Total Features</div>
            </div>
            <div class="bg-purple-50 p-4 rounded-lg">
              <div class="text-purple-800 font-bold text-lg">
                {{ summaryStats.allowedFeatures }}
              </div>
              <div class="text-purple-600">Allowed Features</div>
            </div>
          </div>
        </v-card>
      </div>

      <!-- Modules Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        <v-card
          v-for="(moduleData, moduleName) in Object.fromEntries(
            Object.entries(filteredModules).filter(([key, value]) => value.enable)
          )"
          :key="moduleName"
          class="module-card overflow-hidden border border-gray-200"
        >
          <!-- Module Header -->
          <div
            class="px-4 py-3 border-b border-gray-200 flex justify-between items-center"
            :class="moduleData.enable ? 'bg-green-50' : 'bg-gray-100'"
          >
            <div class="flex items-center">
              <v-icon :color="moduleData.enable ? 'green' : 'grey'" class="mr-2">
                {{ getModuleIcon(moduleName) }}
              </v-icon>
              <h3 class="font-semibold text-gray-800">{{ formatModuleName(moduleName) }}</h3>
            </div>
            <v-chip :color="moduleData.enable ? 'green' : 'grey'" text-color="white" small>
              <v-icon left small>
                {{ moduleData.enable ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off' }}
              </v-icon>
              {{ moduleData.enable ? 'Enabled' : 'Disabled' }}
            </v-chip>
          </div>

          <!-- Module Body -->
          <v-card-text class="p-4">
            <div class="flex justify-between items-center mb-3">
              <h4 class="text-sm font-medium text-gray-600">Features</h4>
              <v-chip color="blue" text-color="white" x-small>
                {{ Object.keys(moduleData?.features || {}).length }}
              </v-chip>
            </div>

            <!-- Features List -->
            <div class="space-y-2">
              <div
                v-if="Object.keys(moduleData?.features || {}).length === 0"
                class="text-sm text-gray-500 italic"
              >
                No features configured
              </div>
              <div
                v-else
                v-for="[featureName, featureStatus] in getVisibleFeatures(
                  moduleData.features,
                  'mod_' + moduleName
                )"
                :key="featureName"
                class="feature-item flex justify-between items-center py-2 px-3 border border-gray-100 rounded hover:bg-gray-50 transition-colors"
              >
                <div class="flex items-center">
                  <v-icon small :color="featureStatus === 'allow' ? 'green' : 'red'" class="mr-2">
                    {{ featureStatus === 'allow' ? 'mdi-check-circle' : 'mdi-lock' }}
                  </v-icon>
                  <span class="text-sm text-gray-700">{{ formatFeatureName(featureName) }}</span>
                </div>
                <v-chip
                  :color="featureStatus === 'allow' ? 'green' : 'red'"
                  text-color="white"
                  x-small
                >
                  {{ featureStatus === 'allow' ? 'Allowed' : 'Denied' }}
                </v-chip>
              </div>
            </div>

            <!-- View More / Less Button -->
            <div
              v-if="hasMoreFeatures(moduleData.features)"
              class="mt-3 text-center border-t border-gray-100 pt-2"
            >
              <button
                @click="toggleCard('mod_' + moduleName)"
                class="text-blue-600 text-xs font-semibold hover:text-blue-800 flex items-center justify-center w-full focus:outline-none"
              >
                {{ expandedCards['mod_' + moduleName] ? 'View Less' : 'View More' }}
                <v-icon x-small color="blue" class="ml-1">
                  {{ expandedCards['mod_' + moduleName] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </button>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Feature Settings & Addons -->
      <div class="mb-8">
        <h2 class="text-2xl font-bold text-gray-800 mb-6">Feature Settings & Addons</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <v-card
            v-for="(categoryData, categoryName) in featureSettings"
            :key="categoryName"
            class="module-card overflow-hidden border border-gray-200"
          >
            <!-- Category Header -->
            <div class="px-4 py-3 border-b border-gray-200 bg-indigo-50">
              <div class="flex items-center">
                <v-icon color="indigo" class="mr-2">mdi-cog</v-icon>
                <h3 class="font-semibold text-indigo-800">{{ formatModuleName(categoryName) }}</h3>
              </div>
            </div>

            <!-- Category Body -->
            <v-card-text class="p-4">
              <div class="flex justify-between items-center mb-3">
                <h4 class="text-sm font-medium text-gray-600">Settings</h4>
                <v-chip color="indigo" text-color="white" x-small>
                  {{ Object.keys(categoryData?.features || {}).length }}
                </v-chip>
              </div>

              <!-- Settings List -->
              <div class="space-y-2">
                <div
                  v-for="[featureName, featureStatus] in getVisibleFeatures(
                    categoryData.features,
                    'cat_' + categoryName
                  )"
                  :key="featureName"
                  class="feature-item flex justify-between items-center py-2 px-3 border border-gray-100 rounded hover:bg-gray-50 transition-colors"
                >
                  <div class="flex items-center">
                    <v-icon small :color="featureStatus === 'allow' ? 'green' : 'red'" class="mr-2">
                      {{ featureStatus === 'allow' ? 'mdi-check-circle' : 'mdi-lock' }}
                    </v-icon>
                    <span class="text-sm text-gray-700">{{ formatFeatureName(featureName) }}</span>
                  </div>
                  <v-chip
                    :color="featureStatus === 'allow' ? 'green' : 'red'"
                    text-color="white"
                    x-small
                  >
                    {{ featureStatus === 'allow' ? 'Allowed' : 'Denied' }}
                  </v-chip>
                </div>
              </div>

              <!-- View More / Less Button -->
              <div
                v-if="hasMoreFeatures(categoryData.features)"
                class="mt-3 text-center border-t border-gray-100 pt-2"
              >
                <button
                  @click="toggleCard('cat_' + categoryName)"
                  class="text-blue-600 text-xs font-semibold hover:text-blue-800 flex items-center justify-center w-full focus:outline-none"
                >
                  {{ expandedCards['cat_' + categoryName] ? 'View Less' : 'View More' }}
                  <v-icon x-small color="blue" class="ml-1">
                    {{
                      expandedCards['cat_' + categoryName] ? 'mdi-chevron-up' : 'mdi-chevron-down'
                    }}
                  </v-icon>
                </button>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </v-container>
    <RazorpayCheckout
      ref="razorpayCheckout"
      :planUserName="userName"
      :planUserEmail="userEmail"
      :planUserContact="userContact"
      :orgInfo="orgInfo"
      :paymentType="billingCycle.toUpperCase()"
    />
  </div>
</template>

<script>
import RazorpayCheckout from '@/components/common/RazorpayCheckout.vue'

export default {
  name: 'ModulePermissionsDashboard',
  components: {
    RazorpayCheckout
  },
  props: { planDetails: Object },
  data() {
    return {
      selectedPlan: 'Base',
      billingCycle: 'monthly', // 'monthly' or 'yearly'
      filteredModules: {},
      expandedCards: {},
      ITEMS_LIMIT: 5,
      loadingPlan: null,
      // Base pricing data
      rawPlans: [
        {
          name: 'Base',
          monthlyPrice: 0,
          yearlyPrice: 0,
          features: [
            'Basic Modules Access',
            'Up to 10 Users',
            'Standard Email Support',
            'Basic Reports'
          ],
          colorName: 'gray',
          popular: false
        },
        {
          name: 'Silver',
          monthlyPrice: 100,
          yearlyPrice: 1000, // Approx 20% off yearly
          features: [
            'All Base Features',
            'Up to 50 Users',
            'Priority Email Support',
            'Advanced Analytics',
            'Payroll Integration'
          ],
          colorName: 'blue',
          popular: true
        },
        {
          name: 'Gold',
          monthlyPrice: 200,
          yearlyPrice: 2000,
          features: [
            'All Silver Features',
            'Up to 200 Users',
            '24/7 Chat Support',
            'Custom Reports',
            'API Access',
            'White Labeling'
          ],
          colorName: 'amber',
          popular: false
        },
        {
          name: 'Diamond',
          monthlyPrice: 500,
          yearlyPrice: 5000,
          features: [
            'All Gold Features',
            'Unlimited Users',
            'Dedicated Account Manager',
            'On-premise Deployment',
            'Custom Development'
          ],
          colorName: 'cyan',
          popular: false
        }
      ]
    }
  },
  computed: {
    currentPlans() {
      return this.rawPlans.map((plan) => ({
        ...plan,
        price: this.billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice,
        priceId: this.billingCycle === 'monthly' ? plan.monthlyPriceId : plan.yearlyPriceId
      }))
    },
    summaryStats() {
      const modules = this.$props.planDetails?.MODULES
      const featureSettingsCategories = this.$props.planDetails?.FEATURE_LIST_AND_SETTINGS

      const totalModules = Object.keys(modules || {})?.length || 0
      const enabledModules = Object.values(modules || {})?.filter((m) => m.enable)?.length || 0
      const modulesWithoutFeatures =
        Object.values(modules || {}).filter(
          (m) => !m.features || Object.keys(m?.features || {}).length === 0
        )?.length || 0

      let totalFeatures = 0
      let allowedFeatures = 0

      if (modules) {
        Object.values(modules).forEach((module) => {
          const features = module.features || {}
          // console.log({ features })
          totalFeatures += Object.keys(features).length
          allowedFeatures += Object.values(features).filter((f) => f === 'allow').length
        })
      }

      if (featureSettingsCategories) {
        Object.values(featureSettingsCategories).forEach((category) => {
          const features = category.features || {}
          // console.log({ features }, '2')
          totalFeatures += Object.keys(features).length
          allowedFeatures += Object.values(features).filter((f) => f === 'allow').length
        })
      }

      return {
        totalModules,
        enabledModules,
        modulesWithoutFeatures,
        totalFeatures,
        allowedFeatures
      }
    },
    featureSettings() {
      return this.$props.planDetails?.FEATURE_LIST_AND_SETTINGS || {}
    },
    currentUser() {
      return this.$storage?.getUniversal('user') || {}
    },
    userName() {
      return (
        `${this.currentUser.first_name || ''} ${this.currentUser.last_name || ''}`.trim() || 'User'
      )
    },
    userEmail() {
      return this.currentUser.email || 'gauravshkl68@gmail.com'
    },
    userContact() {
      return this.currentUser.mobile
    },
    orgInfo() {
      const data = JSON.parse(localStorage.getItem('orgData'))
      return {
        orgId: data?.id,
        planId: data?.plan_id,
        orgName: data?.name
      }
    }
  },
  mounted() {
    this.filteredModules = { ...this.$props.planDetails?.MODULES }
  },
  methods: {
    formatModuleName(name) {
      return name
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase())
    },
    formatFeatureName(name) {
      return name
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase())
    },
    getModuleIcon(moduleName) {
      const icons = {
        AMC: 'mdi-file-document-outline',
        SELLS: 'mdi-cart',
        TASKS: 'mdi-clipboard-list',
        CLAIMS: 'mdi-file-document-edit',
        REPORTS: 'mdi-chart-bar',
        SETTINGS: 'mdi-cog',
        ANALYTICS: 'mdi-chart-line',
        CUSTOMERS: 'mdi-account-group',
        DASHBOARD: 'mdi-view-dashboard',
        EMPLOYEES: 'mdi-account-tie',
        ATTENDANCE: 'mdi-calendar-check',
        NOTIFICATIONS: 'mdi-bell',
        LEAD_MANAGEMENT: 'mdi-target',
        STOCK_MANAGEMENT: 'mdi-package-variant',
        ALLOCATE_PRODUCTS: 'mdi-truck-delivery',
        PROJECT_MANAGEMENT: 'mdi-folder-multiple',
        COLLECTION_MANAGEMENT: 'mdi-cash-multiple'
      }

      return icons[moduleName] || 'mdi-cube'
    },
    toggleCard(id) {
      this.$set(this.expandedCards, id, !this.expandedCards[id])
    },
    getVisibleFeatures(features, id) {
      const entries = Object.entries(features || {})
      if (this.expandedCards[id]) {
        return entries
      }
      return entries.slice(0, this.ITEMS_LIMIT)
    },
    hasMoreFeatures(features) {
      return Object.keys(features || {}).length > this.ITEMS_LIMIT
    },
    async purchasePlan(plan) {
      this.loadingPlan = plan.name

      if (!this.$refs.razorpayCheckout) {
        console.error('Razorpay component ref not found')
        this.loadingPlan = null
        return
      }

      // Prepare plan object for RazorpayCheckout
      const checkoutPlan = {
        name: plan.name,
        // Price should be based on billing cycle
        price: this.billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice,
        id: plan.name.toUpperCase(), // Using name as ID for now or map to specific DB IDs
        billingCycle: this.billingCycle
      }

      try {
        // Use processPayment which handles the full Order -> Checkout -> Verify flow
        await this.$refs.razorpayCheckout.processPayment(checkoutPlan)
      } catch (error) {
        console.error('Payment Flow Error:', error)
        // alert('Something went wrong. Please try again.')
      } finally {
        this.loadingPlan = null
      }
    }
  }
}
</script>

<style scoped>
.feature-item:hover {
  background-color: #f9fafb;
}

.search-field {
  min-width: 250px;
}

.module-card {
  transition: all 0.3s ease;
}

.module-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
</style>
